"use client";

import * as Sentry from "@sentry/nextjs";
import { AlertTriangleIcon } from "lucide-react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { useEffect } from "react";

/**
 * ルート配下(ログイン後の管理画面等)のセグメントエラーバウンダリ。
 * Next.jsの生の例外メッセージ("An error occurred in the Server Components
 * render...")を利用者にそのまま見せない — 本番ビルド(Lambda上のOpenNextは
 * ステージ名がdevでも常に`next build`＝production mode)では詳細メッセージが
 * 意図的に省略され英語の汎用文になるため、代わりにこのアプリ独自の日本語/
 * 多言語対応のエラー表示に置き換える。ルートレイアウト自体が壊れた場合の
 * 最終防衛は`global-error.tsx`（NextIntlClientProviderの外側のため多言語化不可）。
 */
export default function ErrorBoundary({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const t = useTranslations("ErrorBoundary");

  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  return (
    <main className="flex h-screen flex-col items-center justify-center gap-2 px-6 text-center">
      <AlertTriangleIcon className="text-muted" size={64} />
      <h1 className="mt-2 font-medium text-fg text-lg">{t("title")}</h1>
      <p className="max-w-md text-muted text-sm">{t("description")}</p>
      {error.digest && (
        <p className="mt-1 text-[11px] text-placeholder">
          {t("referenceCode", { digest: error.digest })}
        </p>
      )}
      <div className="mt-4 flex gap-2">
        <button
          type="button"
          onClick={reset}
          className="hoverable rounded-md border bg-surface px-4 py-2 text-sm transition-colors"
        >
          {t("retry")}
        </button>
        <Link
          href="/"
          className="hoverable rounded-md bg-fg px-4 py-2 text-sm text-surface transition-colors"
        >
          {t("backToHome")}
        </Link>
      </div>
    </main>
  );
}
